import { FC, CSSProperties, useMemo, ChangeEvent, useRef, useEffect, useState, RefObject, KeyboardEvent, Key, MouseEvent, MutableRefObject, } from "react";
import ReactDOM from 'react-dom';
// 组件
import { CheckOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { ProTag } from "../../../antd";
// 方法
import classNames from "classnames";
import { filterSearchOptions, isOptionSelect, markSearchValue } from "../../util";
// 常量
import { DEFAULT_FIELD_NAMES, SelectDropdownActionType, SelectDropdownBaseProps, SelectOption } from "../../interface";
import { TABLE_COLUMN_EMPTY } from "../../../constants/constant";

interface SelectDropdownProps extends SelectDropdownBaseProps {
  /** 是否显示 */
  show?: boolean;
  /** 距离页面左侧的距离 */
  left?: number;
  /** 距离页面顶侧的距离 */
  top?: number;
  /** 距离页面底侧的距离 */
  bottom?: number;
  /** dropdownRef */
  dropdownRef: RefObject<HTMLDivElement>;
  /** 最小宽度 */
  minWidth: number,
  /** 点击选项 */
  onClickItem: (optionValue: Key | boolean, optionItem: SelectOption) => void;
  /** 点击全选回调 */
  onSelectedAll?: () => void;
  /** 对外方法 */
  actionRef: MutableRefObject<SelectDropdownActionType>;
}

const SelectDropdown: FC<SelectDropdownProps> = props => {
  const {
    value, show, left, top, bottom, showSearch, options, popupClassName, fieldNames, loading, searchPlaceholder, isUpperCase,
    dropdownStyle, minWidth, listHeight, dropdownRef, dropdownFooterRender, isLocalSearch, actionRef, searchDiscriminateCase,
    showAllSelected, zIndex, mode, onClickItem, filterOption, onSearch, getPopupContainer, renderOption, onSelectedAll,
  } = props;
  const [innerOptions, setInnerOptions] = useState<SelectOption[]>(); // 显示出来的选项
  const [activeValue, setActiveValue] = useState<Key | boolean>(); // 当前预选中的值
  const [searchValue, setSearchValue] = useState<string>(); // 搜索内容
  const inputRef = useRef<HTMLInputElement>(null); // 搜索选项输入框
  const timer = useRef<any>(); // 延时过滤选项定时器
  const autoSelected = useRef<boolean>(true); // 是否自动设置第一个选项为active状态
  useEffect(() => {
    if (show) {
      inputRef.current?.focus();
      if (!autoSelected.current) return;
      if (value) {
        if (Array.isArray(value)) {
          setActiveValue(options?.[0]?.[fieldNames?.value || DEFAULT_FIELD_NAMES.value])
        } else {
          setActiveValue(value || options?.[0]?.[fieldNames?.value || DEFAULT_FIELD_NAMES.value]);
        }
      } else {
        setActiveValue(options?.[0]?.[fieldNames?.value || DEFAULT_FIELD_NAMES.value])
      }
    } else {
      autoSelected.current = true;
      setActiveValue(undefined);
    }
  }, [show]);
  useEffect(() => {
    const newOptions = (searchValue && options) ? filterSearchOptions(options, searchValue, fieldNames, searchDiscriminateCase) : options;
    setInnerOptions(newOptions);
    if (Array.isArray(options) && options.length) {
      if (value) {
        if (Array.isArray(value)) {
          setActiveValue(options?.[0]?.[fieldNames?.value || DEFAULT_FIELD_NAMES.value])
        } else {
          setActiveValue(value || options?.[0]?.[fieldNames?.value || DEFAULT_FIELD_NAMES.value]);
        }
      } else {
        setActiveValue(options?.[0]?.[fieldNames?.value || DEFAULT_FIELD_NAMES.value])
      }
    }
  }, [options]);
  /** 键盘事件 */
  useEffect(() => {
    const handleKeydown = (event: any) => {
      if (!innerOptions) return;
      const keyNum = event.key;
      if (keyNum === 'ArrowDown' || keyNum === 'ArrowUp') {
        if (innerOptions.length < 2) return;
        const findIndex = activeValue !== undefined ? innerOptions.findIndex(item => item[fieldNames?.value || DEFAULT_FIELD_NAMES.value] === activeValue) : 0;
        let nextIndex = keyNum === 'ArrowDown' ? findIndex + 1 : findIndex - 1;
        if (nextIndex >= innerOptions.length) nextIndex = 0;
        if (nextIndex < 0) nextIndex = innerOptions.length - 1;
        setActiveValue(innerOptions[nextIndex]?.[fieldNames?.value || DEFAULT_FIELD_NAMES.value]);
        return;
      }
      if (keyNum === 'Enter' && activeValue !== undefined) {
        const findItem = innerOptions?.find(item => item[fieldNames?.value || DEFAULT_FIELD_NAMES.value] === activeValue);
        // setActiveValue(undefined);
        findItem && onClickItem(activeValue, findItem);
      }
    };
    if (show) {
      window.addEventListener('keydown', handleKeydown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [show, innerOptions, activeValue, value]);
  /** 搜索内容改变 */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let filterValue = event.target.value;
    if (isUpperCase) {
      filterValue = filterValue.toUpperCase();
      event.target.value = filterValue;
    }
    setSearchValue(filterValue);
    handleFilterOptions(filterValue?.trim());
  };
  /** 清除搜索内容 */
  const handleClearSearch = (event: MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    handleEmptySearch();
    handleFilterOptions();
  };
  /**
   * 过滤选项
   * @param filterValue 过滤的内容
   * @param isSearch 是否触发onSearch回调
   * @returns 
   */
  const handleFilterOptions = (filterValue?: string, isSearch: boolean = true) => {
    if (isLocalSearch) {
      if (!options) return;
      timer.current && clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const newOptions = filterValue ? filterSearchOptions(options, filterValue, fieldNames, searchDiscriminateCase) : options;
        if (filterValue) {
          setActiveValue(newOptions?.[0]?.[fieldNames?.value || DEFAULT_FIELD_NAMES.value]);
        } else {
          if (Array.isArray(value)) {
            setActiveValue(value[0] as string);
          } else {
            setActiveValue(value || newOptions?.[0]?.[fieldNames?.value || DEFAULT_FIELD_NAMES.value]);
          }
        }
        setInnerOptions(newOptions);
        isSearch && onSearch?.(filterValue);
      }, 300);
      return;
    }
    isSearch && onSearch?.(filterValue);
  };
  /** 鼠标移入选项 */
  const handleItemMouseEnter = (optionValue: string) => {
    autoSelected.current = false;
    setActiveValue(optionValue);
  };
  /** input搜索框键盘事件 */
  const handleInputKeydown = (event: KeyboardEvent<HTMLInputElement>) => {
    const keyNum = event.key;
    if (keyNum === 'ArrowDown' || keyNum === 'ArrowUp') {
      event.preventDefault();
    }
  }
  /** 清空搜索内容 */
  const handleEmptySearch = () => {
    setSearchValue(undefined);
    if (inputRef.current) inputRef.current.value = '';
    // setInnerOptions(options);
  };
  const innerDropdownStyle: CSSProperties = useMemo(() => {
    const newDropdownStyle: CSSProperties = {
      // minWidth,
      width: minWidth,
      transformOrigin: 'center top',
      maxHeight: listHeight,
      zIndex: zIndex || 10001,
      ...dropdownStyle,
    };
    if (!show) {
      newDropdownStyle.display = 'none';
    } else {
      if (left) newDropdownStyle.left = left;
      if (top) newDropdownStyle.top = (top || 0) + 4;
      if (bottom) newDropdownStyle.bottom = bottom;
    }
    return newDropdownStyle;
  }, [show, left, top, bottom, listHeight, zIndex, ]);
  const containerDom: HTMLElement = useMemo(() => {
    if (!getPopupContainer) return document.body;
    if (typeof getPopupContainer === 'function') return getPopupContainer();
    return getPopupContainer;
  }, [getPopupContainer]);
  const dropdownClasses = useMemo(() => {
    return classNames('yb-select-dropdown', popupClassName);
  }, [popupClassName]);
  const renderHTML = (
    <div ref={dropdownRef} className={dropdownClasses} style={innerDropdownStyle} >
      {
        showSearch && (
          <div className="yb-select-dropdown-search">
            <SearchOutlined className="yb-select-dropdown-search-icon" />
            <input
              ref={inputRef}
              value={searchValue}
              className="yb-select-dropdown-search-input"
              type='text'
              autoComplete="off"
              placeholder={searchPlaceholder}
              onChange={handleChange}
              onKeyDown={handleInputKeydown}
            />
            {
              !!searchValue && (
                <CloseOutlined
                  className="yb-select-dropdown-search-close"
                  onClick={handleClearSearch}
                />
              )
            }
          </div>
        )
      }
      <div className="yb-select-dropdown-list">
        {
          loading ? (
            <div className="dropdown-list-empty">
              数据加载中
            </div>
          ) : (
            <>
              {
                innerOptions?.length ? innerOptions.map(option => {
                  if (filterOption) {
                    const isFilter = filterOption(option, value);
                    if (!isFilter) return null;
                  }
                  const {
                    searchLabel,
                    [fieldNames?.label || DEFAULT_FIELD_NAMES.label]: label,
                    [fieldNames?.subLabel || DEFAULT_FIELD_NAMES.subLabel]: subLabel,
                    [fieldNames?.value || DEFAULT_FIELD_NAMES.value]: optionValue,
                    [fieldNames?.children || DEFAULT_FIELD_NAMES.children]: children,
                  } = option;
                  const isSelect = isOptionSelect(value, optionValue);
                  const itemClasses = classNames('dropdown-list-item', {
                    'option-select': isSelect,
                    'option-active': optionValue === activeValue,
                  });
                  const formatLabel = markSearchValue(searchLabel || label, searchValue, searchDiscriminateCase);
                  const formatSubLabel = markSearchValue(subLabel, searchValue, searchDiscriminateCase);
                  if (Array.isArray(children)) {
                    return (
                      <div key={optionValue} className="dropdown-list-items">
                        <div className="dropdown-list-items-title">
                          <span className="option-title">{label || TABLE_COLUMN_EMPTY}</span>
                          {!!subLabel && <span className="option-sub-title">{subLabel}</span>}
                        </div>
                        <div className="dropdown-list-items-list">
                          {
                            children.length ? children.map(child => {
                              if (filterOption) {
                                const isFilterChildren = filterOption(option, value);
                                if (!isFilterChildren) return null;
                              }
                              const {
                                [fieldNames?.childrenLabel || DEFAULT_FIELD_NAMES.childrenLabel]: childrenLable,
                                [fieldNames?.childrenSubLabel || DEFAULT_FIELD_NAMES.childrenSubLabel]: childrenSubLabel,
                                [fieldNames?.childrenValue || DEFAULT_FIELD_NAMES.childrenValue]: childrenOptionValue,
                              } = child;
                              const isChildrenSelect = isOptionSelect(value, childrenOptionValue);
                              const childrenItemClasses = classNames('dropdown-list-item', {
                                'option-select': isChildrenSelect,
                                'option-active': childrenOptionValue === value,
                              });
                              const formatChildrenLabel = markSearchValue(childrenLable, searchValue, searchDiscriminateCase);
                              const formatChildrenSubLabel = markSearchValue(childrenSubLabel, searchValue, searchDiscriminateCase);
                              return (
                                <div
                                  key={childrenOptionValue}
                                  className={childrenItemClasses}
                                  onClick={() => onClickItem(childrenOptionValue, child)}
                                  onMouseEnter={() => handleItemMouseEnter(childrenOptionValue as string)}
                                >
                                  <div className="dropdown-list-item-option yb-line-ellipsis">
                                    {
                                      renderOption ? renderOption(child) : (
                                        <>
                                          {
                                            typeof formatChildrenLabel === 'string' ? (
                                              <span className="option-title" title={childrenLable} dangerouslySetInnerHTML={{ __html: formatChildrenLabel }}></span>
                                            ) : (
                                              <span className="option-title">{formatChildrenLabel}</span>
                                            )
                                          }
                                          {
                                            typeof formatChildrenSubLabel === 'string' ? (
                                              <span className="option-sub-title" title={childrenSubLabel} dangerouslySetInnerHTML={{ __html: formatChildrenSubLabel }}></span>
                                            ) : (
                                              <span className="option-sub-title">{formatChildrenSubLabel}</span>
                                            )
                                          }
                                        </>
                                      )
                                    }
                                  </div>
                                  {
                                    isChildrenSelect && (
                                      <div className="dropdown-list-item-select">
                                        <CheckOutlined />
                                      </div>
                                    )
                                  }
                                </div>
                              );
                            }) : (
                              <div className="dropdown-list-empty">
                                暂无数据
                              </div>
                            )
                          }
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={optionValue}
                      className={itemClasses}
                      onClick={() => onClickItem(optionValue, option)}
                      onMouseEnter={() => handleItemMouseEnter(optionValue as string)}
                    >
                      <div className="dropdown-list-item-option yb-line-ellipsis">
                        {
                          renderOption ? renderOption(option) : (
                            <>
                              {
                                typeof formatLabel === 'string' ? (
                                  <span className="option-title" title={label} dangerouslySetInnerHTML={{ __html: formatLabel }}></span>
                                ) : (
                                  <span className="option-title">{formatLabel}</span>
                                )
                              }
                              {
                                typeof formatSubLabel === 'string' ? (
                                  <span className="option-sub-title" title={subLabel} dangerouslySetInnerHTML={{ __html: formatSubLabel }}></span>
                                ) : (
                                  <span className="option-sub-title">{formatSubLabel}</span>
                                )
                              }
                            </>
                          )
                        }

                      </div>
                      {
                        isSelect && (
                          <div className="dropdown-list-item-select">
                            <CheckOutlined />
                          </div>
                        )
                      }
                    </div>
                  );
                }) : (
                  <div className="yb-select-dropdown-empty">
                    {
                      searchValue ? '没有已搜索内容匹配的选项' : '暂无数据'
                    }
                  </div>
                )
              }
            </>
          )
        }
      </div>
      {
        (!!dropdownFooterRender || (mode === 'multiple' && showAllSelected)) && (
          <div className="yb-select-dropdown-footer">
            {dropdownFooterRender}
            {
              showAllSelected && (
                <ProTag
                  className="move-pointer"
                  onClick={onSelectedAll}
                >全选</ProTag>
              )
            }
          </div>
        )
      }
    </div>
  );
  actionRef.current = {
    onEmptySearch: handleEmptySearch,
  };
  return ReactDOM.createPortal(renderHTML, containerDom);
};

export default SelectDropdown;
